import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./QuickLinks.css";
import "./EventCalendar.css";
import antiragging from "../assests/img/antiragging.png";

const BASE_URL = process.env.REACT_APP_API_URL || "http://192.168.0.29:5001/api";

function slugify(name = "") {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function pageHref(item, parentPath = "") {
  if (item.page_url) return item.page_url;
  const slug = slugify(item.page_name);
  return parentPath ? `/${parentPath}/${slug}` : `/${slug}`;
}

function getChildren(item) {
  return item.children || item.sub_menus || item.submenu || item.child_pages || item.subpages || [];
}

const STUDENT_CORNER_LINKS = [
  {
    page_id: "menu1",
    page_name: "Students Discipline Guidelines",
    children: [
      { page_id: "menu1-sub1", page_name: "Rules For Boarders Of Hostel", page_url: "/students-discipline-guidelines/rules-for-boarders-of-hostel" },
      { page_id: "menu1-sub2", page_name: "Rules in General For all Students", page_url: "/students-discipline-guidelines/rules-in-general-for-all-students" },
    ],
  },
  {
    page_id: "menu2",
    page_name: "Students Union",
    children: [
      {
        page_id: "menu2-sub1",
        page_name: "UnderGraduate Students Union",
        children: [
          { page_id: "menu2-sub1-sub1", page_name: "UG 2024-25", page_url: "/students-union/undergraduate-students-union/ug-2024-25" },
        ],
      },
      {
        page_id: "menu2-sub2",
        page_name: "Junior Doctors Association",
        children: [
          { page_id: "menu2-sub2-sub1", page_name: "2024-25", page_url: "/students-union/junior-doctors-association/2024-25" },
        ],
      },
    ],
  },
  {
    page_id: "menu3",
    page_name: "New Registration",
    page_url: "/StudentRegistration",
  },
  {
    page_id: "menu4",
    page_name: "Student Login",
    page_url: "/StudentLogin",
  },
];

// ── Faculty Corner: just the box + two submenu rows for now.
// Target pages aren't defined yet — url left as "#" as a placeholder
// until the actual routes are decided.

const FACULTY_CORNER_LINKS = [
  {
    page_id: "faculty-menu1",
    page_name: "View By Designation",
    page_url: "/faculty/designation",
  },
  {
    page_id: "faculty-menu2",
    page_name: "View By Department",
    page_url: "/faculty/department",
  },
];

// ── Admissions: single direct row, no submenu. Target page isn't
// defined yet — swap in real page_url once routes are decided.

const ADMISSIONS_LINKS = [
  {
    page_id: "admissions-menu1",
    page_name: "Admissions",
    page_url: "/admissions",
  },
];

// ── Govt. Approval: single direct row, no submenu. Target page
// isn't defined yet — swap in real page_url once routes are decided.

const GOVT_APPROVAL_LINKS = [
  {
    page_id: "govt-approval-menu1",
    page_name: "Govt. Approval",
    page_url: "/govt-approval",
  },
];

function QlNode({ item, parentPath, level, depth, openPath, toggle, onNavigate }) {
  const children = getChildren(item);
  const hasChildren = children.length > 0;
  const path = parentPath ? `${parentPath}/${slugify(item.page_name)}` : slugify(item.page_name);
  const childLevel = `${level}>${item.page_id}`;
  const isOpen = openPath[level] === item.page_id;

  return (
    <li className={`ql-node depth-${depth}${isOpen ? " open" : ""}`}>
      <button
        className="ql-row"
        onClick={() => {
          if (hasChildren) toggle(level, item.page_id);
          else onNavigate(pageHref(item, parentPath));
        }}
      >
        <span className="ql-row-label">{item.page_name}</span>
        {hasChildren && (
          <span className="ql-row-toggle" aria-hidden="true">
            {isOpen ? "\u2212" : "+"}
          </span>
        )}
      </button>

      {hasChildren && isOpen && (
        <ul className="ql-sublist">
          {children.map((child) => (
            <QlNode
              key={child.page_id}
              item={child}
              parentPath={path}
              level={childLevel}
              depth={depth + 1}
              openPath={openPath}
              toggle={toggle}
              onNavigate={onNavigate}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

// ── Event calendar (year-wise, backend-driven, hover tooltip) ─────────────
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function buildMonthGrid(year, month) {
  const firstOfMonth = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startWeekday = firstOfMonth.getDay();

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  return cells;
}

function EventCalendar() {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [events, setEvents] = useState([]);
  const [hoveredDate, setHoveredDate] = useState(null);
  const [loading, setLoading] = useState(false);

  const cells = buildMonthGrid(viewYear, viewMonth);

  useEffect(() => {
    setLoading(true);
    setHoveredDate(null);

    fetch(`${BASE_URL}/events?year=${viewYear}&month=${viewMonth + 1}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (Array.isArray(data)) setEvents(data);
      })
      .catch((err) => {
        console.error("Events fetch failed:", err.message);
        setEvents([]);
      })
      .finally(() => setLoading(false));
  }, [viewYear, viewMonth]);

  const eventsByDate = React.useMemo(() => {
    const map = {};
    events.forEach((e) => {
      const d = new Date(e.date);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map[key]) map[key] = [];
      map[key].push(e);
    });
    return map;
  }, [events]);

  const getEventsFor = (date) => {
    const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    return eventsByDate[key] || [];
  };

  const goPrevMonth = () => {
    setViewMonth((m) => {
      if (m === 0) {
        setViewYear((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
  };

  const goNextMonth = () => {
    setViewMonth((m) => {
      if (m === 11) {
        setViewYear((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
  };

  const goPrevYear = () => setViewYear((y) => y - 1);
  const goNextYear = () => setViewYear((y) => y + 1);

  return (
    <div className="ec-widget">
      <div className="ec-widget-head">
        <i className="fa-solid fa-calendar-days" aria-hidden="true" />
        <span>Events Calendar</span>
      </div>

      <div className="ec-nav">
        <div className="ec-nav-group">
          <button className="ec-nav-btn ec-nav-btn-sm" onClick={goPrevYear} aria-label="Previous year">
            &laquo;
          </button>
          <button className="ec-nav-btn" onClick={goPrevMonth} aria-label="Previous month">
            &lsaquo;
          </button>
        </div>

        <span className="ec-month-label">
          {MONTH_NAMES[viewMonth]} {viewYear}
        </span>

        <div className="ec-nav-group">
          <button className="ec-nav-btn" onClick={goNextMonth} aria-label="Next month">
            &rsaquo;
          </button>
          <button className="ec-nav-btn ec-nav-btn-sm" onClick={goNextYear} aria-label="Next year">
            &raquo;
          </button>
        </div>
      </div>

      <div className="ec-grid ec-grid-labels">
        {DAY_LABELS.map((d) => (
          <div key={d} className="ec-day-label">{d}</div>
        ))}
      </div>

      {loading ? (
        <div className="ec-loading">Loading events…</div>
      ) : (
        <div className="ec-grid">
          {cells.map((date, i) => {
            if (date === null) {
              return <div key={`blank-${i}`} className="ec-cell ec-cell-empty" />;
            }

            const dayEvents = getEventsFor(date);
            const hasEvent = dayEvents.length > 0;
            const isHovered =
              hoveredDate && isSameDay(hoveredDate, date) && hasEvent;

            return (
              <div
                key={date.toISOString()}
                className={`ec-cell${isSameDay(date, today) ? " ec-today" : ""}${
                  hasEvent ? " ec-has-event" : ""
                }`}
                onMouseEnter={() => hasEvent && setHoveredDate(date)}
                onMouseLeave={() => setHoveredDate(null)}
              >
                <span className="ec-cell-num">{date.getDate()}</span>
                {hasEvent && <span className="ec-event-dot" />}

                {isHovered && (
                  <div className="ec-tooltip">
                    {dayEvents.map((e) => (
                      <div key={e.id} className="ec-tooltip-item">
                        <div className="ec-tooltip-title">{e.title}</div>
                        {e.description && (
                          <div className="ec-tooltip-desc">{e.description}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function QuickLinks() {
  const [openPath, setOpenPath] = useState({});
  const [antiRaggingNo, setAntiRaggingNo] = useState(null);
  const navigate = useNavigate();

  const toggle = (level, id) => {
    setOpenPath((prev) => {
      const next = {};
      for (const key of Object.keys(prev)) {
        if (key === level || key.startsWith(`${level}>`)) continue;
        next[key] = prev[key];
      }
      if (prev[level] !== id) {
        next[level] = id;
      }
      return next;
    });
  };

  useEffect(() => {
    fetch(`${BASE_URL}/parameters/30`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (data && data.value) setAntiRaggingNo(data.value);
      })
      .catch((err) => console.error("Anti-ragging number fetch failed:", err.message));
  }, []);

  return (
    <>
      <div className="ql-widget">
        <div className="ql-widget-head">
          <i className="fa-solid fa-user-graduate" aria-hidden="true" />
          <span>Student Corner</span>
        </div>

        <ul className="ql-list">
          {STUDENT_CORNER_LINKS.map((menu) => (
            <QlNode
              key={menu.page_id}
              item={menu}
              parentPath=""
              level="root"
              depth={0}
              openPath={openPath}
              toggle={toggle}
              onNavigate={navigate}
            />
          ))}

          <li className="ql-node ec-node">
            <EventCalendar />
          </li>

          {/* <li className="ql-node">
            <div className="anti-ragging-box">
              <div className="anti-title">ANTI-RAGGING</div>
              <div className="anti-subtitle">Toll free Number to Report Ragging:</div>
              <div className="phone-row">
                <div className="phone-icon">
                  <i className="fa-solid fa-phone-volume"></i>
                </div>
                <div className="phone-number">{antiRaggingNo || "1800-180-5522"}</div>
                <div className="available">(24 x 7)</div>
              </div>
            </div>
          </li> */}

          <li className="ql-node">
            <div
              className="anti-ragging-box"
              role="button"
              tabIndex={0}
              onClick={() => navigate("/anti-ragging")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") navigate("/anti-ragging");
              }}
              style={{ cursor: "pointer" }}
            >
              <div className="anti-title">ANTI-RAGGING</div>
              <div className="anti-subtitle">Toll free Number to Report Ragging:</div>
              <div className="phone-row">
                <div className="phone-icon">
                  <i className="fa-solid fa-phone-volume"></i>
                </div>
                <div className="phone-number">{antiRaggingNo || "1800-180-5522"}</div>
                <div className="available">(24 x 7)</div>
              </div>
            </div>
          </li>
        </ul>
      </div>

      {/* Admissions — single direct row, no submenu.
          Target page isn't defined yet; onNavigate is wired but
          the link's page_url is a placeholder until routes
          are decided. */}
      <div className="ql-widget ad-widget">
        <div className="ql-widget-head">
          <i className="fa-solid fa-user-plus" aria-hidden="true" />
          <span>Admissions</span>
        </div>

        <ul className="ql-list">
          {ADMISSIONS_LINKS.map((menu) => (
            <QlNode
              key={menu.page_id}
              item={menu}
              parentPath=""
              level="admissions-root"
              depth={0}
              openPath={openPath}
              toggle={toggle}
              onNavigate={navigate}
            />
          ))}
        </ul>
      </div>

      {/* Govt. Approval — single direct row, no submenu.
          Target page isn't defined yet; onNavigate is wired but
          the link's page_url is a placeholder until routes
          are decided. */}
      <div className="ql-widget ga-widget">
        <div className="ql-widget-head">
          <i className="fa-solid fa-landmark" aria-hidden="true" />
          <span>Govt. Approval</span>
        </div>

        <ul className="ql-list">
          {GOVT_APPROVAL_LINKS.map((menu) => (
            <QlNode
              key={menu.page_id}
              item={menu}
              parentPath=""
              level="govt-approval-root"
              depth={0}
              openPath={openPath}
              toggle={toggle}
              onNavigate={navigate}
            />
          ))}
        </ul>
      </div>

      {/* Faculty Corner — box + two submenu rows only for now.
          Target pages aren't defined yet; onNavigate is wired but
          each link's page_url is a "#" placeholder until routes
          are decided. */}
      <div className="ql-widget fc-widget">
        <div className="ql-widget-head">
          <i className="fa-solid fa-chalkboard-user" aria-hidden="true" />
          <span>Faculties</span>
        </div>

        <ul className="ql-list">
          {FACULTY_CORNER_LINKS.map((menu) => (
            <QlNode
              key={menu.page_id}
              item={menu}
              parentPath=""
              level="faculty-root"
              depth={0}
              openPath={openPath}
              toggle={toggle}
              onNavigate={navigate}
            />
          ))}
        </ul>
      </div>
    </>
  );
}
